import { REFRESH_COLOURS } from '../../pedigree-sidebar/SidebarSection';
import { PREG_ACCENT } from '../../pedigree-sidebar/sections/clinical-notes-section/recordCardStyles';

/**
 * Colour for the pedigree context menu's icons.
 *
 * Colour does exactly ONE job here: it marks the group. If each of the fourteen
 * items got its own hue the menu becomes a rainbow and the colour stops carrying
 * information. It is applied to the ICON only — the labels stay neutral, so a run
 * of rows still reads as a list rather than as fourteen coloured strings.
 *
 * Values come from the sidebar rather than being invented. `tileTheme.js` records
 * why: brand teal (`theme.accent`, default #2ba9bf) manages about 2.9:1 on white,
 * under both the 4.5:1 text floor and the 3:1 that WCAG 1.4.11 asks of a non-text
 * UI component — and an 18px pedigree glyph stroked at 1.75px is exactly that
 * case. `REFRESH_COLOURS.labelActive` is the darkened teal that clears it at
 * 5.7:1. The same file records what happened when a first attempt used the
 * configured button colour: on a navy-branded deployment it produced a wall of
 * dark blocks. These are fixed values for that reason — they must not track
 * `theme.accent`.
 */
export const MENU_TONE = {
  /** Relationships — anything that adds a node or changes an edge. */
  relationship: REFRESH_COLOURS.labelActive,

  /** This person — attributes that change how the existing symbol is drawn. */
  person: REFRESH_COLOURS.label,

  /** Annotate, and actions on the node rather than the person's data. */
  neutral: REFRESH_COLOURS.icon,

  /** Delete, and nothing else. */
  danger: 'error.main',

  /**
   * Reproductive — a deliberate exception to the group rule, because the sidebar
   * already marks reproductive data with this and consistency ACROSS surfaces
   * beats consistency within the menu.
   *
   * Note this is `PREG_ACCENT` (#c2185b), the pink the pregnancy tiles, the
   * prenatal chips and `ReproLauncher` already use. The brief called it violet;
   * the established sidebar colour is this pink, and the brief's own stated
   * reason — match what the sidebar established — points here.
   */
  reproductive: PREG_ACCENT
};

/**
 * Menu width. The old menu was 170px, which predates icons: an 18px glyph, a
 * gutter, "Grandparents" and a right-aligned "recorded" do not fit in it. Sized
 * against the longest real string, which is "Link to Existing…" — not anything in
 * the mockups.
 */
export const MENU_WIDTH = 244;

/**
 * Popper modifiers for every menu level.
 *
 * There were none. A right-click near the bottom of the viewport ran the menu off
 * the screen, and a flyout opened near the right edge did the same sideways —
 * which is a live defect, not a consequence of the redesign: the menu already
 * clipped at 442px. The design chose icons-and-hairlines over labelled bands
 * partly to keep the height down, and this is what actually makes that argument
 * moot.
 *
 * `altAxis` on preventOverflow is what lets a tall menu slide UP rather than
 * hang off the bottom; `flip` alone only swaps the placement.
 */
export const MENU_MODIFIERS = [
  { name: 'flip', enabled: true },
  {
    name: 'preventOverflow',
    enabled: true,
    options: { altAxis: true, padding: 8 }
  }
];

/** Flyouts additionally fall back to the left when there is no room on the right. */
export const SUBMENU_MODIFIERS = [
  {
    name: 'flip',
    enabled: true,
    options: { fallbackPlacements: ['left-start', 'right-end', 'left-end'] }
  },
  {
    name: 'preventOverflow',
    enabled: true,
    options: { altAxis: true, padding: 8 }
  }
];
